// RevenueShareChart.tsx
import React, { memo, useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

interface RevenueItem {
  label: string;
  value: number;
  color: string;
}

interface IProps {
  data: RevenueItem[];
  itemHeight?: number;
  formatValue?(value: number): string | number;
}

const XYChart: React.FC<IProps> = ({ data, itemHeight = 40, formatValue }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        paddingLeft: 20
      })
    );

    chart.zoomOutButton.set('forceHidden', true);

    const yRenderer = am5xy.AxisRendererY.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true
    });

    yRenderer.grid.template.set('location', 1);

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: 'label',
        renderer: yRenderer
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        min: 0,
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: '#,###a'
        }),
        extraMax: 0.1,
        renderer: am5xy.AxisRendererX.new(root, {
          strokeOpacity: 0.1,
          minGridDistance: 80
        })
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Series 1',
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: 'value',
        categoryYField: 'label'
      })
    );

    series.columns.template.setAll({
      strokeOpacity: 0,
      height: itemHeight
    });

    series.bullets.push(() => {
      const label = am5.Label.new(root, {
        centerY: am5.p50,
        centerX: am5.p0,
        populateText: true,
        fontSize: 12,
        fill: am5.color('#000') // adjust as needed
      });

      // Dynamically format the label text
      label.adapters.add('text', (_, target) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const value = target.dataItem?.dataContext?.value;

        return formatValue ? `${formatValue(value)}` : `${value}`;
      });

      return am5.Bullet.new(root, {
        locationX: 1,
        sprite: label
      });
    });

    series.columns.template.adapters.add('fill', (fill, target) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const color = target.dataItem?.dataContext?.color;

      return am5.color(color || '#cccccc');
    });

    series.columns.template.adapters.add('stroke', (stroke, target) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const color = target.dataItem?.dataContext?.color;

      return am5.color(color || '#cccccc');
    });

    series.columns.template.adapters.add('stroke', function (stroke, target) {
      return chart.get('colors')?.getIndex(series.columns.indexOf(target));
    });

    yAxis.data.setAll(data);
    series.data.setAll(data);

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis: xAxis,
        yAxis: yAxis
      })
    );

    series.appear(1000);
    chart.appear(1000, 100);

    return () => root.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div
      id="revenue-chart"
      className="analytics-chart-card"
      ref={chartRef}
      style={{ width: '100%', height: `${data.length * itemHeight * 2}px` }}
    />
  );
};

export default memo(XYChart);
